"use client";

import { useState } from "react";
import { IBase } from "@umahealth/entities";
import {
	DialogContent,
	DialogOverlay,
	DialogPortal,
	DialogRoot,
	DialogTitle,
} from "@umahealth/occipital/client";
import { Button, Title, Paragraph } from "@umahealth/occipital";

interface BaseSelectModalProps {
	bases: IBase[];
	currentBaseId: number | null;
	/**
	 * La base donde la persona está asignada según el legajo.
	 *
	 * Va primera en la lista para encontrarla rápido, pero **no** viene
	 * preseleccionada. Medido sobre prod el 7/9, últimos 30 días: de 83
	 * enfermeros, 73 trabajan siempre en una sola base y para 58 de esos 73 esa
	 * base no es la del legajo. O sea que el campo está mal cargado para el
	 * 70%, y preseleccionarlo convertía un clic distraído en un dato erróneo
	 * escrito en el turno. Vacío se nota; mal, no.
	 *
	 * Cuando el legajo esté corregido esto puede volver a preseleccionarse.
	 */
	baseHabitual?: number | null;
	/** Al abrir el turno la pregunta es otra que al cambiar de lugar. */
	abriendoTurno?: boolean;
	onSelect: (baseId: number) => void;
	onClose: () => void;
}

export default function BaseSelectModal({
	bases,
	currentBaseId,
	baseHabitual = null,
	abriendoTurno = false,
	onSelect,
	onClose,
}: BaseSelectModalProps) {
	// Sólo se preselecciona lo que la persona ya declaró en este turno; el dato
	// del legajo no alcanza para dar una respuesta por ella (ver `baseHabitual`).
	const [selected, setSelected] = useState<number | null>(currentBaseId);

	// La habitual primero; el resto en el orden que ya venía.
	const ordenadas = [...bases].sort((a, b) =>
		a.id === baseHabitual ? -1 : b.id === baseHabitual ? 1 : 0,
	);

	/*
	 * Va por el Dialog de occipital, que monta en un portal al final del body.
	 *
	 * Antes era un `div` con `fixed inset-0 z-50` escrito acá mismo, es decir
	 * dentro del sidebar. Y el sidebar vive en un contenedor `sticky`, que crea
	 * su propio contexto de apilado: el `z-50` del modal sólo competía adentro
	 * de ese contexto, no contra la página. Como el sidebar aparece antes en el
	 * DOM y no tiene `z-index` propio, todo lo que viene después se dibujaba
	 * encima — el buscador de operadores y el encabezado sticky de la tabla
	 * atravesaban el modal y el botón de confirmar quedaba tapado.
	 *
	 * El mismo problema y el mismo arreglo que el modal del QR en
	 * TrenesOperationsColumns, con los mismos z-index para que convivan.
	 */
	return (
		<DialogRoot
			defaultOpen
			modal
			onOpenChange={(abierto) => {
				if (!abierto) onClose();
			}}
		>
			<DialogPortal>
				<DialogOverlay className="z-[100]" />
				<DialogContent className="z-[101] max-w-md flex flex-col gap-4">
					<DialogTitle asChild>
						{/* `pr-6` para no pasar por debajo de la cruz de cerrar. */}
						<Title
							hierarchy="h2"
							size="text-m"
							color="text-grey-800"
							weight="font-semibold"
							className="pr-6"
						>
							{abriendoTurno ? "¿Dónde trabajás hoy?" : "Cambiar de lugar"}
						</Title>
					</DialogTitle>
					<Paragraph className="text-grey-500 text-sm">
						{abriendoTurno
							? "Elegí el CRPC o el móvil donde vas a estar. Queda registrado en el turno, y si te movés lo podés cambiar desde la casita del menú."
							: "Elegí el CRPC o el móvil donde estás atendiendo ahora."}
					</Paragraph>

					{/* `min-h-0` para que la lista sea la que scrollea y no empuje
					    los botones fuera del modal. */}
					<div className="flex flex-col gap-2 overflow-y-auto min-h-0">
						{ordenadas.map((base) => (
							<button
								key={base.id}
								type="button"
								onClick={() => setSelected(base.id)}
								className={`text-left px-4 py-3 rounded-lg border transition-colors ${
									selected === base.id
										? "border-primary-700 bg-primary-25 text-primary-800"
										: "border-grey-200 hover:border-grey-300 text-grey-700"
								}`}
							>
								<span className="font-medium">{base.name}</span>
								{base.CRPC && (
									<span className="text-xs text-grey-400 ml-2">
										{base.CRPC}
									</span>
								)}
								{base.id === baseHabitual && (
									<span className="text-xs text-grey-400 ml-2">
										· tu base
									</span>
								)}
							</button>
						))}
					</div>

					{/*
					 * Uno arriba del otro, no lado a lado: compartiendo la fila el
					 * confirmar quedaba en media columna del modal, chico para el dedo
					 * en una tablet. Ahora ocupa el ancho completo y "Cerrar" queda
					 * abajo, sin peso visual.
					 */}
					<div className="flex flex-col gap-1">
						<Button
							className="bg-primary-700 text-white"
							size="full"
							type="button"
							disabled={!selected}
							onClick={() => {
								if (selected) onSelect(selected);
							}}
						>
							Confirmar
						</Button>
						<Button
							className="text-grey-500"
							size="full"
							variant="text"
							type="button"
							onClick={onClose}
						>
							{abriendoTurno ? "Ahora no" : "Cerrar"}
						</Button>
					</div>
				</DialogContent>
			</DialogPortal>
		</DialogRoot>
	);
}
